import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import { Play, Save } from 'lucide-react'
import Editor from '@monaco-editor/react'
import type * as Monaco from 'monaco-editor'
import { schemaApi, dataSourceApi } from '@/api/services'
import { useToast } from '@/context/ToastContext'
import { Button } from '@/components/ui/Button'
import { Badge, Spinner } from '@/components/ui/misc'
import { DataSourceParameterToggles } from '@/components/DataSourceParameterToggles'
import { SnippetPicker, SNIPPET_DRAG_TYPE } from '@/components/SnippetPicker'
import { shouldShowParameterPlaceholderHint } from '@/lib/injectedParameters'
import {
  hasOptionalParameters,
  mergeInputParameterFlags,
  OPTIONAL_PARAMETER_SQL_HINT_LINES,
} from '@/lib/sqlPlaceholders'
import { cn } from '@/lib/utils'
import { formatApiError, toastApiError } from '@/lib/apiError'
import type { DataSource } from '@/types'

interface Props {
  dataSource: Partial<DataSource> | null
  onSaved: () => void
}

export interface SqlEditorHandle {
  insertText: (text: string) => void
}

const PLACEHOLDER = `-- Drag columns from the Schema panel on the left
SELECT r."Name", r."Gender"
FROM "Residents" r
WHERE r."IsActive" = true
-- Use {{DateFrom}} and {{DateTo}} for date range filters`

export const SqlEditor = forwardRef<SqlEditorHandle, Props>(function SqlEditor({ dataSource, onSaved }, ref) {
  const { toast } = useToast()
  const editorRef = useRef<Monaco.editor.IStandaloneCodeEditor | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useImperativeHandle(ref, () => ({
    insertText(text: string) {
      const editor = editorRef.current
      if (!editor) return
      const selection = editor.getSelection()
      if (!selection) return
      editor.executeEdits('snippet-insert', [{ range: selection, text, forceMoveMarkers: true }])
      editor.focus()
    },
  }))

  const [key, setKey] = useState(dataSource?.key ?? '')
  const [sql, setSql] = useState(dataSource?.sqlQuery ?? '')
  const [isCollection, setIsCollection] = useState(dataSource?.isCollection ?? true)
  const [inputParameters, setInputParameters] = useState(() =>
    mergeInputParameterFlags(dataSource?.sqlQuery ?? '', dataSource?.inputParameters ?? [])
  )
  const [previewing, setPreviewing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[] | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  useEffect(() => {
    setKey(dataSource?.key ?? '')
    setSql(dataSource?.sqlQuery ?? '')
    setIsCollection(dataSource?.isCollection ?? true)
    setInputParameters(
      mergeInputParameterFlags(dataSource?.sqlQuery ?? '', dataSource?.inputParameters ?? [])
    )
  }, [dataSource])

  // Handle column drop from schema explorer
  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setIsDragOver(false)
    const snippetContent = e.dataTransfer.getData(SNIPPET_DRAG_TYPE)
    if (snippetContent && editorRef.current) {
      const editor = editorRef.current
      const selection = editor.getSelection()
      if (selection) {
        editor.executeEdits('snippet-drop', [{ range: selection, text: snippetContent, forceMoveMarkers: true }])
        editor.focus()
      }
      return
    }
    const text = e.dataTransfer.getData('text/x-sql-column')
    if (!text || !editorRef.current) return
    const editor = editorRef.current
    const selection = editor.getSelection()
    if (!selection) return
    editor.executeEdits('drop', [{ range: selection, text, forceMoveMarkers: true }])
    editor.focus()
  }

  function handleMount(editor: Monaco.editor.IStandaloneCodeEditor, monaco: typeof Monaco) {
    editorRef.current = editor

    // Register Postgres-flavoured SQL language tokens
    monaco.languages.register({ id: 'pgsql-rs' })

    monaco.languages.setMonarchTokensProvider('pgsql-rs', {
      defaultToken: '',
      ignoreCase: true,
      tokenizer: {
        root: [
          // Line comments
          [/--.*$/, 'comment'],
          // Block comments
          [/\/\*/, 'comment', '@blockComment'],
          // Liquid placeholders {{DateFrom}}
          [/\{\{[^}]+\}\}/, 'liquid.placeholder'],
          // Strings
          [/'([^'\\]|\\.)*'/, 'string'],
          [/\$\$/, 'string', '@dollarString'],
          // Numbers
          [/\b\d+(\.\d+)?(e[+-]?\d+)?\b/i, 'number'],
          // Dollar params
          [/\$\d+/, 'variable'],
          // Keywords
          [/\b(SELECT|FROM|WHERE|JOIN|LEFT|RIGHT|INNER|OUTER|FULL|CROSS|ON|AS|AND|OR|NOT|IN|EXISTS|BETWEEN|LIKE|ILIKE|IS|NULL|TRUE|FALSE|CASE|WHEN|THEN|ELSE|END|GROUP\s+BY|ORDER\s+BY|HAVING|LIMIT|OFFSET|UNION|ALL|DISTINCT|INSERT|INTO|VALUES|UPDATE|SET|DELETE|CREATE|ALTER|DROP|TABLE|VIEW|INDEX|SCHEMA|WITH|CTE|RETURNING|COALESCE|NULLIF|CAST|OVER|PARTITION\s+BY|ROW_NUMBER|RANK|DENSE_RANK|LAG|LEAD|SUM|COUNT|AVG|MIN|MAX|ARRAY_AGG|STRING_AGG|NOW|CURRENT_DATE|CURRENT_TIMESTAMP|DATE_PART|DATE_TRUNC|AGE|EXTRACT|INTERVAL|TO_CHAR|TO_DATE|TRIM|LOWER|UPPER|LENGTH|CONCAT|SPLIT_PART|REPLACE|REGEXP_REPLACE|JSONB_BUILD_OBJECT|JSON_AGG)\b/, 'keyword'],
          // Schema-qualified identifiers: public."Table"
          [/\b[\w]+(?=\.)/, 'identifier.schema'],
          // Quoted identifiers
          [/"([^"\\]|\\.)*"/, 'identifier.quoted'],
          // Operators
          [/[=<>!:]+/, 'operator'],
          [/[-+*/%&|^~]/, 'operator'],
          // Punctuation
          [/[();,.]/, 'delimiter'],
          // Plain identifiers
          [/[a-zA-Z_]\w*/, 'identifier'],
          [/\s+/, ''],
        ],
        blockComment: [
          [/\*\//, 'comment', '@pop'],
          [/./, 'comment'],
        ],
        dollarString: [
          [/\$\$/, 'string', '@pop'],
          [/./, 'string'],
        ],
      },
    })

    monaco.editor.defineTheme('sql-dark', {
      base: 'vs-dark',
      inherit: true,
      rules: [
        { token: 'keyword',              foreground: '4f8ef7', fontStyle: 'bold' },
        { token: 'identifier.quoted',    foreground: '3ecf8e' },
        { token: 'identifier.schema',    foreground: 'a0b4d0' },
        { token: 'identifier',           foreground: 'e2e8f8' },
        { token: 'string',               foreground: 'f7c948' },
        { token: 'number',               foreground: 'f7c948' },
        { token: 'variable',             foreground: 'f75f5f' },
        { token: 'comment',              foreground: '5a6480', fontStyle: 'italic' },
        { token: 'operator',             foreground: '7c5cfc' },
        { token: 'delimiter',            foreground: '5a6480' },
        { token: 'liquid.placeholder',   foreground: '3ecf8e', fontStyle: 'bold' },
      ],
      colors: {
        'editor.background':               '#0d0f14',
        'editor.foreground':               '#e2e8f8',
        'editor.lineHighlightBackground':  '#13161d',
        'editorLineNumber.foreground':     '#2a3147',
        'editorLineNumber.activeForeground': '#5a6480',
        'editor.selectionBackground':      '#4f8ef730',
        'editorCursor.foreground':         '#4f8ef7',
        'editorIndentGuide.background1':   '#1f2433',
        'editorBracketMatch.background':   '#4f8ef720',
        'editorBracketMatch.border':       '#4f8ef7',
      },
    })

    monaco.editor.setTheme('sql-dark')
  }

  /** Selection runs as a partial query; empty selection runs the full editor text. */
  function getQueryToRun(): string {
    const editor = editorRef.current
    if (!editor) return sql.trim()

    const model = editor.getModel()
    if (!model) return sql.trim()

    const fullText = model.getValue()
    const sel = editor.getSelection()
    if (!sel || sel.isEmpty()) return fullText.trim()

    const fragment = model.getValueInRange(sel).trim()
    return fragment.length > 0 ? fragment : fullText.trim()
  }

  async function runPreview() {
    const query = getQueryToRun()
    if (!query) return
    setPreviewing(true); setPreviewError(null); setPreviewRows(null)
    try {
      const res = await schemaApi.preview(query)
      setPreviewRows(res.rows)
    } catch (e: unknown) {
      setPreviewError(formatApiError(e, 'Query failed'))
    } finally { setPreviewing(false) }
  }

  async function save() {
    if (!key || !sql) return toast('Key and SQL are required', 'error')
    setSaving(true)
    try {
      const payload = {
        key: key.trim().toLowerCase(),
        description: '',
        sqlQuery: sql,
        isCollection,
        inputParameters: mergeInputParameterFlags(sql, inputParameters),
      }
      if (dataSource?.id) await dataSourceApi.update(dataSource.id, payload)
      else await dataSourceApi.create(payload)
      toast(`"${key}" saved`)
      onSaved()
    } catch (e: unknown) {
      toastApiError(toast, e, 'Save failed')
    }
    finally { setSaving(false) }
  }

  const columns = previewRows?.[0] ? Object.keys(previewRows[0]) : []

  /** F5 runs preview; capture phase + preventDefault stops the browser refresh while this panel is focused. */
  function handleF5RunCapture(e: React.KeyboardEvent) {
    if (e.key !== 'F5') return
    e.preventDefault()
    e.stopPropagation()
    if (previewing) return
    void runPreview()
  }

  return (
    <div className="flex h-full flex-col" onKeyDownCapture={handleF5RunCapture}>
      {/* Toolbar */}
      <div className="flex items-center gap-3 border-b border-border bg-surface px-4 py-2 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-bold tracking-widest uppercase text-muted">KEY</span>
          <input
            className="w-28 rounded border border-border2 bg-bg px-2 py-1 text-xs font-mono text-green focus:border-green focus:outline-none"
            placeholder="residents"
            value={key}
            onChange={e => setKey(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsCollection(p => !p)}
            className={cn(
              'relative h-5 w-10 flex-shrink-0 rounded-full transition-colors',
              isCollection ? 'bg-accent' : 'bg-border2'
            )}
          >
            <span
              className={cn(
                'pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                isCollection ? 'translate-x-5' : 'translate-x-0'
              )}
            />
          </button>
          <span className="text-[10px] text-muted font-mono">{isCollection ? 'array' : 'object'}</span>
        </div>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="sm"
          onClick={runPreview}
          disabled={previewing}
          title="Run selected SQL, or the full query if nothing is selected (F5)"
        >
          {previewing ? <Spinner className="h-3 w-3" /> : <Play className="h-3 w-3" />}
          Run
        </Button>
        <Button size="sm" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : <><Save className="h-3 w-3" /> Save</>}
        </Button>
      </div>

      <div className="border-b border-border bg-surface px-4 py-2 flex-shrink-0">
        <SnippetPicker
          type="DataSource"
          compact
          onInsert={text => {
            const editor = editorRef.current
            if (!editor) {
              const next = sql ? `${sql}\n${text}` : text
              setSql(next)
              setInputParameters(prev => mergeInputParameterFlags(next, prev))
              return
            }
            const selection = editor.getSelection()
            if (!selection) return
            editor.executeEdits('snippet-insert', [{ range: selection, text, forceMoveMarkers: true }])
            editor.focus()
          }}
        />
      </div>

      {/* Monaco SQL editor */}
      <div
        ref={containerRef}
        className={cn(
          'flex-1 overflow-hidden transition-colors',
          isDragOver && 'ring-1 ring-inset ring-accent/40 bg-accent/5'
        )}
        onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; setIsDragOver(true) }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
      >
        <Editor
          height="100%"
          language="pgsql-rs"
          theme="sql-dark"
          value={sql || ''}
          onChange={v => {
            const next = v ?? ''
            setSql(next)
            setInputParameters(prev => mergeInputParameterFlags(next, prev))
          }}
          onMount={handleMount}
          loading={
            <div className="flex h-full items-center justify-center bg-bg text-xs text-muted font-mono">
              Loading editor…
            </div>
          }
          options={{
            fontSize: 12,
            fontFamily: "'JetBrains Mono', monospace",
            lineHeight: 20,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            tabSize: 2,
            renderLineHighlight: 'line',
            smoothScrolling: true,
            cursorBlinking: 'smooth',
            cursorSmoothCaretAnimation: 'on',
            padding: { top: 12, bottom: 12 },
            scrollbar: { verticalScrollbarSize: 6, horizontalScrollbarSize: 6 },
            placeholder: sql ? undefined : PLACEHOLDER,
          }}
        />
      </div>

      <div className="border-t border-border bg-surface px-4 py-3 flex-shrink-0 max-h-44 overflow-y-auto">
        <span className="text-[10px] font-bold tracking-widest uppercase text-muted">SQL parameters</span>
        <div className="mt-2">
          <DataSourceParameterToggles params={inputParameters} onChange={setInputParameters} />
          {shouldShowParameterPlaceholderHint(inputParameters) ? (
            <p className="text-[10px] text-muted font-mono">
              Use {'{{Name}}'} placeholders in the query; they appear here for required/optional.
            </p>
          ) : null}
          {hasOptionalParameters(inputParameters) ? (
            <div className="mt-2 space-y-1 rounded border border-border2 bg-bg/40 p-2">
              {OPTIONAL_PARAMETER_SQL_HINT_LINES.map(line => (
                <p key={line} className="text-[10px] text-muted font-mono leading-relaxed">
                  {line}
                </p>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      {/* Preview results */}
      <div className="border-t border-border flex-shrink-0">
        <div className="flex items-center gap-2 px-4 py-2 bg-surface">
          <span className="text-[10px] font-bold tracking-widest uppercase text-muted">Preview</span>
          {previewRows && <Badge variant="green">{previewRows.length} rows</Badge>}
        </div>
        {previewError ? (
          <p className="px-4 py-2 text-xs text-red font-mono">{previewError}</p>
        ) : previewRows ? (
          <div className="overflow-x-auto max-h-36 overflow-y-auto">
            <table className="w-full text-[10px] font-mono border-collapse">
              <thead>
                <tr>{columns.map(c => <th key={c} className="sticky top-0 bg-surface text-left px-3 py-1.5 text-muted border-b border-border">{c}</th>)}</tr>
              </thead>
              <tbody>
                {previewRows.map((row, i) => (
                  <tr key={i} className="hover:bg-surface">
                    {columns.map(c => <td key={c} className="px-3 py-1 border-b border-border/50 text-ink">{String(row[c] ?? '')}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-4 py-3 text-[10px] text-muted font-mono">Run a query to see results</p>
        )}
      </div>
    </div>
  )
})

SqlEditor.displayName = 'SqlEditor'